import { getSharePipe } from '@context/useShareContext/methods/shareInitialization';
import { getOrderPipe } from '@pages/Basket/useBasket';
import { useEffect, useState } from 'react';
import {
  BehaviorSubject,
  combineLatest,
  distinctUntilChanged,
  filter,
  first,
  withLatestFrom,
} from 'rxjs';

import { getServicePipe } from './additionServicesContext';
import restoreServiceData from './methods/basket/restoreServiceData';
import { SetUpServiceData } from './methods/basket/types';

/**
 * WidgetTab описывает типизацию данных вкладок для виджета
 */
export type WidgetTab = {
  label: string;
  data: any;
  type: 'delivery' | 'service';
};

/**
 * WidgetTabsContext описывает контекст для работы с вкладками
 * виджета.
 */
export interface WidgetTabsContext {
  IsLoading: boolean; // Флаг загрузки вкладок
  ActiveTab: number; // Активная вкладка
  ActiveTabData: WidgetTab; // Данные активной вкладки
  Tabs: WidgetTab[]; // Все доступные вкладки
}

// Инициализация стейта табулятора виджета.
// Данные по умолчанию подтягиваются из кэша в localStorage.
const cache = localStorage.getItem('___ONLOG_WIDGET___TABS_CTX');
export const tabsContext$ = new BehaviorSubject<WidgetTabsContext>({
  IsLoading: true,
  Tabs: [
    {
      label: 'TubsContainer:Delivery',
      data: undefined,
      type: 'delivery',
    },
  ],
  ...(JSON.parse(cache?.length > 0 ? cache : '{}') as Partial<WidgetTabsContext>),
  ActiveTab: 0,
  ActiveTabData: {
    label: 'TubsContainer:Delivery',
    data: undefined,
    type: 'delivery',
  },
});

/**
 * onChangeTab выполняет изменение активной вкладки по ее номеру
 * @param tab
 * @param isResetShareParam
 */
const onChangeTab = (tab: number) => {
  const state = tabsContext$.getValue();
  const tabData = state.Tabs[tab];

  if (!tabData) {
    return;
  }

  if (state.ActiveTab === tab) {
    return;
  }

  tabsContext$.next({
    ...state,
    ActiveTab: tab,
    ActiveTabData: tabData,
  });
};

/**
 * pushTabs добавляет вкладки в стейт. Заменяет данные, если они уже
 * есть в стейте.
 * @param tabs
 */
export const pushTabs = (tabs: WidgetTab[]) => {
  const state = tabsContext$.getValue();
  const newTabs: WidgetTab[] = [
    {
      label: 'TubsContainer:Delivery',
      data: undefined,
      type: 'delivery',
    },
    ...tabs,
  ];

  let selectedTab = newTabs.findIndex((tab) => tab.data?.id === state.ActiveTabData.data?.id);
  let selectedTabData = newTabs[selectedTab];

  if (selectedTab == -1) {
    selectedTab = 0;
    selectedTabData = newTabs[0];
  }

  tabsContext$.next({
    ...state,
    IsLoading: false,
    Tabs: newTabs,
    ActiveTabData: selectedTabData,
    ActiveTab: selectedTab,
  });
};

/**
 * getTabsPipe возвращает subscriber для возможности подписки
 * на изменение активной вкладки и других данных.
 */
export const getTabsPipe = () => {
  return tabsContext$.pipe();
};

/**
 * init выполняет инициализацию контекста для работы с вкладками.
 */
const init = () => {
  // Сохранение контекста в localStorage
  tabsContext$.pipe().subscribe({
    next: (state) => {
      localStorage.setItem('___ONLOG_WIDGET___TABS_CTX', JSON.stringify(state));
    },
  });

  // Подписка на cтейт шаринга.
  // Выполняется один раз при инциализации параметра ShareTab, больше он мененяется во время выполнения.
  // Меняет активную табу и её данные.
  getSharePipe()
    .pipe(withLatestFrom(getTabsPipe()))
    .pipe(
      distinctUntilChanged(
        ([prevShare, prevTabs], [currShare, currTabs]) =>
          prevShare.ShareTab === currShare.ShareTab && prevTabs.IsLoading === currTabs.IsLoading
      )
    )
    .subscribe(([share, tabs]) => {
      const activeTabIndex = tabs.Tabs.findIndex((tab) => tab.data?.id === share.ShareTab);

      if (activeTabIndex !== -1) {
        tabsContext$.next({
          ...tabsContext$.getValue(),
          ActiveTab: activeTabIndex,
          ActiveTabData: tabs.Tabs[activeTabIndex],
        });
      }
    });

  // Подписка на cтейты табов, заказа и шаринга.
  // Выполняется один при условии, что табы не загружаются и существует один из способов восстановления
  // доп. услуг.
  // Меняет активную табу и её данные.
  combineLatest([getTabsPipe(), getOrderPipe(), getSharePipe()])
    .pipe(withLatestFrom(getServicePipe()))
    .pipe(filter(([_, service]) => service.IsRestoringState))
    .pipe(
      first(
        ([[tabs, order, share]]) => !tabs.IsLoading && (!!order.editProduct || !!share.ShareType)
      )
    )
    .subscribe({
      next: ([[tabs, order, share]]) => {
        const restoredOrder =
          order?.editProduct && restoreServiceData(order.editProduct).setUpService;
        const restoredShare = (Array.isArray(share.ShareData) ? share.ShareData : []).find(
          (service: SetUpServiceData) => !!service?.IsSetupService
        ) as SetUpServiceData;

        const restoredActiveTab =
          restoredOrder?.ServicesPath?.[0] ?? restoredShare?.ServicesPath?.[0];

        if (!restoredActiveTab) {
          if (share.ShareType === 'delivery') {
            tabsContext$.next({
              ...tabsContext$.getValue(),
              ActiveTab: 0,
              ActiveTabData: tabs.Tabs['0'],
            });
          }
          return;
        }

        const activeTabIndex = tabs.Tabs.findIndex((tab) => tab.data?.id === restoredActiveTab.id);

        if (activeTabIndex !== -1) {
          tabsContext$.next({
            ...tabsContext$.getValue(),
            ActiveTab: activeTabIndex,
            ActiveTabData: tabs.Tabs[activeTabIndex],
          });
        }
      },
    });
};

const useWidgetTabsContext = () => {
  const [tabs, setTabs] = useState(tabsContext$.getValue());
  useEffect(() => {
    const subscription = tabsContext$.pipe().subscribe({
      next: (state) => setTabs({ ...state }),
    });

    return () => {
      try {
        subscription.unsubscribe();
      } catch (e) {}
    };
  }, []);

  return {
    tabs,
    init,
    onChangeTab,
  };
};

export default useWidgetTabsContext;
